import {
	type AlertColorTypes,
	ClipboardActions,
	NETWORK_RECIPIENTS_LABELS,
	type NetworkType,
	pasteTextFromClipboard,
	RecipientTypes,
	type TranslationIDs,
	truncateString,
	useIntl,
	VALID_ADDRESS_REGEX
} from '@capsicum/common';
import {
	Alert,
	Button,
	Divider,
	NetworkIcon,
	RadioGroupInput,
	Skeleton,
	SkeletonItem,
	Text,
	Textarea,
	type TextareaIcon,
	TextButton,
	TextInput,
	useCopyToClipboard
} from '@capsicum/common-web';
import { AlertIcon, CopyClipboardIcon, PasteIcon } from '@capsicum/common-web/icons';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { object, string, z } from 'zod';

const MAX_EXCHANGE_INPUT_LENGTH = 15;
const MAX_NAME_INPUT_LENGTH = 28;

export type RecipientFormShape = {
	address: string;
	exchange: string;
	network: string;
	recipientName: string;
	type: RecipientTypes;
};

export type RecipientFormProps = {
	alertText?: string;
	defaultValues: Partial<RecipientFormShape>;
	events?: {
		addressEvents?: (type: ClipboardActions) => void;
		exchangeEvents?: () => void;
		recipientNameEvents?: () => void;
		typeEvents?: (type: RecipientTypes) => void;
	};
	isEdit?: boolean;
	isSaving?: boolean;
	networkType: NetworkType;
	onSecondaryClick?: () => void;
	onSubmitForm: (data: RecipientFormShape) => void;
	secondaryButtonLabel?: string;
	submitButtonLabel: string;
	testID?: string;
};

export const RecipientFormSkeleton = ({ isEdit = false, secondaryButtonLabel = '', testID = '' }) => {
	const { $t } = useIntl();

	const texts = {
		type: $t('withdrawal.recipientForm.type')
	};

	return (
		<Skeleton>
			<S.FormContainer>
				<S.TextInputSkeleton />
				<div>
					<Divider orientation="horizontal" />
					<S.RadioGroupInputContainer>
						<Text variant="text3">{texts.type}</Text>
						<S.CheckboxesSkeleton />
					</S.RadioGroupInputContainer>
					<Divider orientation="horizontal" />
				</div>
				<S.TextInputSkeleton $withSupportingText />
				<S.NetWorkInputWrapper>
					<S.TextInputSkeleton />
				</S.NetWorkInputWrapper>
				<S.BlockchainAddressInputWrapper $isReadOnly={isEdit}>
					<S.TextInputSkeleton $withSupportingText />
				</S.BlockchainAddressInputWrapper>

				<S.ButtonSection>
					<S.CreateButtonWrapper>
						<Button disabled isLoading testID={testID + '-skeletonSubmitButton'} />
					</S.CreateButtonWrapper>
				</S.ButtonSection>
			</S.FormContainer>
			<TextButton
				color="neutral"
				disabled
				isLoading
				testID={testID + '-skeletonCancelButton'}
				text={secondaryButtonLabel}
			/>
		</Skeleton>
	);
};

export const RecipientForm = ({
	alertText,
	defaultValues,
	events,
	isEdit = false,
	isSaving,
	networkType,
	onSecondaryClick,
	onSubmitForm,
	secondaryButtonLabel,
	submitButtonLabel,
	testID = 'recipientForm'
}: RecipientFormProps) => {
	const { $t } = useIntl();

	const schema = useMemo(() => {
		return object({
			address: string()
				.regex(
					!isEdit ? VALID_ADDRESS_REGEX : /.*/,
					$t('withdrawal.recipientForm.incorrectAddress' as TranslationIDs, {
						network: NETWORK_RECIPIENTS_LABELS[networkType]
					})
				)
				.nonempty($t('withdrawal.recipientForm.required')),
			exchange: string()
				.max(
					MAX_EXCHANGE_INPUT_LENGTH,
					$t('withdrawal.recipientForm.charactersLimitMessage', { limitNumber: MAX_EXCHANGE_INPUT_LENGTH })
				)
				.nonempty($t('withdrawal.recipientForm.required')),
			network: string(),
			recipientName: string()
				.max(
					MAX_NAME_INPUT_LENGTH,
					$t('withdrawal.recipientForm.charactersLimitMessage', { limitNumber: MAX_NAME_INPUT_LENGTH })
				)
				.nonempty($t('withdrawal.recipientForm.required')),
			type: z.union([z.literal(RecipientTypes.PERSONAL), z.literal(RecipientTypes.EXCHANGE)])
		});
	}, [$t, isEdit, networkType]);

	const form = useForm<RecipientFormShape>({
		defaultValues: {
			...defaultValues,
			address:
				// To show the formatted blockchain address for mobile layout on the input and then send the defaultValues.address on submit
				isEdit && defaultValues.address ? truncateString(defaultValues.address, 8, 8) : defaultValues.address
		},
		mode: 'onChange',
		resolver: zodResolver(schema)
	});

	const texts = {
		addressIsCopied: $t('withdrawal.recipientForm.addressIsCopied'),
		blockchainAddress: $t('withdrawal.recipientForm.blockchainAddress', {
			network: NETWORK_RECIPIENTS_LABELS[networkType]
		}),
		blockchainAddressSupportingText: $t('withdrawal.recipientForm.blockchainAddressSupportingText'),
		exchangeLabel: $t('withdrawal.recipientForm.exchangeLabel'),
		networkLabel: $t('withdrawal.recipientForm.networkLabel'),
		pasteBlockchainAddress: $t('withdrawal.recipientForm.pasteBlockchainAddress', {
			network: NETWORK_RECIPIENTS_LABELS[networkType]
		}),
		personalLabel: $t('withdrawal.recipientForm.personalLabel'),
		recipientName: $t('withdrawal.recipientForm.recipientName'),
		type: $t('withdrawal.recipientForm.type'),
		walletOrExchangeLabel: $t('withdrawal.recipientForm.walletOrExchangeLabel'),
		walletOrExchangeLabelSupportingText: $t('withdrawal.recipientForm.walletOrExchangeLabelSupportingText')
	};

	const { copyToClipboard } = useCopyToClipboard({
		label: texts.addressIsCopied,
		value: isEdit ? defaultValues.address || '' : form.getValues('address')
	});

	const doCopyToClipboard = async () => {
		events?.addressEvents?.(ClipboardActions.COPY);
		await form.trigger('address');

		if (!form.getFieldState('address').invalid) copyToClipboard();
	};

	const doPasteFromClipboard =
		pasteTextFromClipboard &&
		(async () => {
			events?.addressEvents?.(ClipboardActions.PASTE);
			form.setValue('address', await pasteTextFromClipboard!());
			await form.trigger('address');
		});

	const showSecondaryButton = secondaryButtonLabel && !!onSecondaryClick;

	const addressTextareaIcon: TextareaIcon | undefined = isEdit
		? {
				icon: CopyClipboardIcon,
				onClick: doCopyToClipboard,
				testID: testID + '-address-copyButton'
			}
		: doPasteFromClipboard
			? {
					icon: PasteIcon,
					onClick: doPasteFromClipboard,
					testID: testID + '-address-pasteButton'
				}
			: undefined;

	return (
		<>
			<form
				onSubmit={form.handleSubmit(data => {
					form.formState.isValid &&
						onSubmitForm(!isEdit ? data : { ...data, address: defaultValues.address || '' });
				})}
			>
				<FormProvider {...form}>
					<S.FormContainer>
						<TextInput<RecipientFormShape>
							label={texts.recipientName}
							name="recipientName"
							testID={testID + '-recipientName-textInput'}
							onClick={events?.recipientNameEvents}
						/>
						<div>
							<Divider orientation="horizontal" />
							<S.RadioGroupInputContainer>
								<Text variant="text3">{texts.type}</Text>
								<RadioGroupInput<RecipientFormShape>
									isChip
									name="type"
									options={[
										{ id: RecipientTypes.PERSONAL, label: texts.personalLabel },
										{ id: RecipientTypes.EXCHANGE, label: texts.exchangeLabel }
									]}
									row
									testID={testID + '-type-radioInput'}
									onChangeSelected={option => {
										events?.typeEvents?.(option as RecipientTypes);
									}}
								/>
							</S.RadioGroupInputContainer>
							<Divider orientation="horizontal" />
						</div>
						<TextInput<RecipientFormShape>
							label={texts.walletOrExchangeLabel}
							name="exchange"
							supportingText={texts.walletOrExchangeLabelSupportingText}
							testID={testID + '-exchange-textInput'}
							onClick={events?.exchangeEvents}
						/>
						<S.NetWorkInputWrapper>
							<TextInput<RecipientFormShape>
								defaultValue={NETWORK_RECIPIENTS_LABELS[networkType]}
								inlineIcon={<NetworkIcon network={networkType} size="small" variant="circle" />}
								label={texts.networkLabel}
								name="network"
								readonly
								testID={testID + '-network-textInput'}
							/>
						</S.NetWorkInputWrapper>
						<S.BlockchainAddressInputWrapper $isReadOnly={isEdit}>
							<Textarea<RecipientFormShape>
								iconEnd={addressTextareaIcon}
								isMultiline={!isEdit}
								label={isEdit ? texts.blockchainAddress : texts.pasteBlockchainAddress}
								name="address"
								readonly={isEdit}
								supportingText={texts.blockchainAddressSupportingText}
								testID={testID + '-address-textInput'}
								onClick={async () => {
									!isEdit && (await doPasteFromClipboard?.());
								}}
							/>
						</S.BlockchainAddressInputWrapper>
						{alertText && (
							<Alert
								closable={false}
								icon={AlertIcon}
								subTitle={alertText}
								testID={testID + '-alert'}
								type={'important' as AlertColorTypes}
							/>
						)}
						<S.ButtonSection>
							<S.CreateButtonWrapper>
								<Button
									disabled={!form.formState.isValid}
									isLoading={isSaving}
									testID={testID + '-submitButton'}
									text={submitButtonLabel}
								/>
							</S.CreateButtonWrapper>
						</S.ButtonSection>
					</S.FormContainer>
				</FormProvider>
			</form>
			{showSecondaryButton && (
				<TextButton
					color="neutral"
					testID={testID + '-cancelButton'}
					text={secondaryButtonLabel}
					onClick={onSecondaryClick}
				/>
			)}
		</>
	);
};

namespace S {
	export const FormContainer = styled.div`
		display: flex;
		justify-content: center;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xsmall};
		margin-bottom: ${({ theme }) => theme.spacings.small};
	`;
	export const NetWorkInputWrapper = styled.div`
		cursor: not-allowed;
	`;
	export const BlockchainAddressInputWrapper = styled.div<{ $isReadOnly: boolean }>`
		cursor: ${({ $isReadOnly }) => ($isReadOnly ? 'not-allowed' : 'default')};
	`;
	export const RadioGroupInputContainer = styled.div`
		display: flex;
		align-items: center;
		gap: ${({ theme }) => theme.spacings.xxsmall};
		padding: ${({ theme }) => theme.spacings.xxsmall} 0px;
	`;
	export const ButtonSection = styled.div`
		width: 100%;
		display: flex;
		justify-content: center;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.large};
	`;
	export const CreateButtonWrapper = styled.div`
		margin: 0 auto;
	`;

	export const CheckboxesSkeleton = styled(SkeletonItem)`
		height: 32px;
		width: 200px;
	`;
	export const TextInputSkeleton = styled(SkeletonItem)<{ $withSupportingText?: boolean }>`
		height: ${({ $withSupportingText }) => ($withSupportingText ? '74px' : '64px')};
	`;
}
